"use client";

import { useState } from "react";
import {
  BUTTON_SECONDARY,
  BUTTON_SMALL,
  CARD,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import type { DOSAGE_CALCULATION_CALCULATOR_STRINGS } from "@/components/tools/dosage-calculation-calculator";
import {
  computeConversion,
  computeDesiredHave,
  computeWeight,
  type DosageContext,
  type DosageOutput,
  formLabel,
  type SupplyForm,
} from "@/components/tools/stats-clinical/dosage-compute";
import { t } from "@/lib/i18n/interpolate";

/*
 * Textbook-style practice problems: a fixed list of invented orders, shown one at a time,
 * with the working hidden until asked for. Nothing is random, so the server and the browser
 * render the same first problem. No drug is named.
 */

type Strings = typeof DOSAGE_CALCULATION_CALCULATOR_STRINGS;

type Problem =
  | {
      kind: "dh";
      desired: number;
      desiredUnit: string;
      have: number;
      haveUnit: string;
      quantity: number;
      form: SupplyForm;
    }
  | {
      kind: "weight";
      perKg: number;
      unit: string;
      weight: number;
      weightUnit: "kg" | "lb";
      have: number;
      haveUnit: string;
      quantity: number;
    }
  | { kind: "day"; perKg: number; unit: string; weight: number; doses: number }
  | { kind: "convert"; value: number; from: string; to: string };

const PROBLEMS: readonly Problem[] = [
  {
    kind: "dh",
    desired: 500,
    desiredUnit: "mg",
    have: 250,
    haveUnit: "mg",
    quantity: 1,
    form: "tablet",
  },
  {
    kind: "dh",
    desired: 0.25,
    desiredUnit: "g",
    have: 125,
    haveUnit: "mg",
    quantity: 1,
    form: "tablet",
  },
  {
    kind: "dh",
    desired: 75,
    desiredUnit: "mg",
    have: 25,
    haveUnit: "mg",
    quantity: 5,
    form: "ml",
  },
  {
    kind: "weight",
    perKg: 15,
    unit: "mg",
    weight: 22,
    weightUnit: "lb",
    have: 100,
    haveUnit: "mg",
    quantity: 5,
  },
  { kind: "day", perKg: 2, unit: "mg", weight: 30, doses: 2 },
  {
    kind: "dh",
    desired: 0.1,
    desiredUnit: "mg",
    have: 0.4,
    haveUnit: "mg",
    quantity: 1,
    form: "ml",
  },
  { kind: "convert", value: 2.5, from: "kg", to: "g" },
  { kind: "convert", value: 2, from: "tsp", to: "mL" },
];

function describe(
  problem: Problem,
  strings: Strings,
  ctx: DosageContext
): { question: string; output: DosageOutput | null } {
  const fmt = (value: number) => ctx.fmt(value, { maximumFractionDigits: 3 });
  if (problem.kind === "dh") {
    return {
      question: t(strings.problemDh, {
        desired: fmt(problem.desired),
        desiredUnit: problem.desiredUnit,
        have: fmt(problem.have),
        haveUnit: problem.haveUnit,
        quantity: fmt(problem.quantity),
        form: formLabel(strings, problem.form, ctx.locale, problem.quantity),
      }),
      output: computeDesiredHave(problem, ctx),
    };
  }
  if (problem.kind === "weight") {
    return {
      question: t(strings.problemWeight, {
        perKg: fmt(problem.perKg),
        unit: problem.unit,
        weight: fmt(problem.weight),
        weightUnit: problem.weightUnit,
        have: fmt(problem.have),
        haveUnit: problem.haveUnit,
        quantity: fmt(problem.quantity),
      }),
      output: computeWeight(
        {
          perKg: problem.perKg,
          doseUnit: problem.unit,
          weight: problem.weight,
          weightUnit: problem.weightUnit,
          basis: "dose",
          dosesPerDay: 1,
          supply: {
            have: problem.have,
            haveUnit: problem.haveUnit,
            quantity: problem.quantity,
          },
        },
        ctx
      ),
    };
  }
  if (problem.kind === "day") {
    return {
      question: t(strings.problemDay, {
        perKg: fmt(problem.perKg),
        unit: problem.unit,
        weight: fmt(problem.weight),
        doses: fmt(problem.doses),
      }),
      output: computeWeight(
        {
          perKg: problem.perKg,
          doseUnit: problem.unit,
          weight: problem.weight,
          weightUnit: "kg",
          basis: "day",
          dosesPerDay: problem.doses,
          supply: null,
        },
        ctx
      ),
    };
  }
  return {
    question: t(strings.problemConvert, {
      value: fmt(problem.value),
      from: problem.from,
      to: problem.to,
    }),
    output: computeConversion(problem.value, problem.from, problem.to, ctx),
  };
}

export function DosagePractice({
  strings,
  fmt,
  locale,
}: {
  strings: Strings;
  fmt: (value: number, options?: Intl.NumberFormatOptions) => string;
  locale: string;
}) {
  const [index, setIndex] = useState(0);
  const [revealed, setRevealed] = useState(false);
  const ctx: DosageContext = { strings, fmt, locale, decimals: 2 };
  const { question, output } = describe(PROBLEMS[index], strings, ctx);
  return (
    <section className={`${CARD} space-y-4`}>
      <h3 className={WIDGET_H3}>
        {t(strings.practiceHeading, {
          number: fmt(index + 1),
          total: fmt(PROBLEMS.length),
        })}
      </h3>
      <p className="text-foreground text-sm leading-relaxed">{question}</p>
      <div className="flex flex-wrap gap-3">
        <button
          aria-expanded={revealed}
          className={BUTTON_SMALL}
          onClick={() => setRevealed((value) => !value)}
          type="button"
        >
          {revealed ? strings.hideAnswer : strings.showAnswer}
        </button>
        <button
          className={BUTTON_SECONDARY}
          onClick={() => {
            setIndex((value) => (value + 1) % PROBLEMS.length);
            setRevealed(false);
          }}
          type="button"
        >
          {strings.nextProblem}
        </button>
      </div>
      {revealed && output ? (
        <div aria-live="polite" className="space-y-1">
          {output.steps.map((step) => (
            <p
              className="font-mono text-foreground text-sm leading-relaxed"
              key={step}
            >
              {step}
            </p>
          ))}
          <p className="font-semibold text-foreground text-sm">
            {t(strings.answerLine, {
              answer: output.cards.map(([, value]) => value).join("; "),
            })}
          </p>
        </div>
      ) : null}
      <p className="text-muted-foreground text-xs">{strings.practiceNote}</p>
    </section>
  );
}
